import type { ReactNode } from 'react';

interface CpmErrorCardProps {
    title: string;
    body: string;
    onRetry?: () => void;
    retryLabel?: string;
    retrying?: boolean;
    className?: string;
    children?: ReactNode;
}

/**
 * Full-width shortfall error card with optional retry CTA.
 * Matches Volume Settings / Overview boot-error chrome in the CPM design pack.
 */
export default function CpmErrorCard({
    title,
    body,
    onRetry,
    retryLabel = 'Try again',
    retrying = false,
    className = '',
    children,
}: CpmErrorCardProps) {
    return (
        <div
            className={[
                'flex flex-col items-center text-center rounded-[16px] border-[1.5px] border-[var(--cpm-shortfall-border)] bg-[var(--cpm-shortfall-bg)]',
                className,
            ].join(' ')}
            style={{ gap: 14, padding: '40px 32px' }}
            role="alert"
        >
            <span
                className="inline-flex items-center justify-center rounded-[18px] border border-[var(--cpm-shortfall-border)] bg-white text-[var(--cpm-shortfall-text)]"
                style={{ width: 72, height: 72 }}
            >
                <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                </svg>
            </span>
            <h3 className="m-0 !text-[20px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-shortfall-text)]">
                {title}
            </h3>
            <p className="m-0 max-w-[480px] !text-[13.5px] !font-semibold !leading-[1.55] !text-[var(--cpm-shortfall-text-med)]">
                {body}
            </p>
            {onRetry ? (
                <button
                    type="button"
                    onClick={onRetry}
                    disabled={retrying}
                    className="inline-flex items-center rounded-[10px] bg-[var(--cpm-brand-navy)] text-white shadow-[0_8px_20px_-6px_rgba(14,8,88,.60)] hover:brightness-110 disabled:cursor-progress"
                    style={{
                        gap: 8,
                        height: 42,
                        padding: '0 20px',
                        fontSize: 13.5,
                        fontWeight: 800,
                        opacity: retrying ? 0.75 : 1,
                    }}
                >
                    {retrying ? (
                        <span className="cpm-spin-w" />
                    ) : (
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                            <path d="M3 3v5h5" />
                        </svg>
                    )}
                    {retrying ? 'Retrying' : retryLabel}
                </button>
            ) : null}
            {children}
        </div>
    );
}
